import React, { useState, useRef, useEffect } from 'react';
// Using inline SVG icons to match existing component patterns

interface IframeLoaderProps {
  src: string;
  title: string;
  height?: string;
  onError?: (error: string) => void;
}

export const IframeLoader: React.FC<IframeLoaderProps> = ({
  src,
  title,
  height = '600px',
  onError
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      console.log('Iframe loaded successfully');
      setLoading(false);
      setError(null);
      
      // Clear timeout if iframe loads successfully
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const handleError = () => {
      const errorMsg = 'Failed to load analytics dashboard';
      console.error('Iframe load error');
      setError(errorMsg);
      setLoading(false);
      onError?.(errorMsg);
      
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    // Set up event listeners
    iframe.addEventListener('load', handleLoad);
    iframe.addEventListener('error', handleError);

    // Set up timeout fallback (30 seconds)
    timeoutRef.current = setTimeout(() => {
      if (loading) {
        const timeoutMsg = 'Dashboard loading timeout - please check your connection';
        console.warn('Iframe loading timeout');
        setError(timeoutMsg);
        setLoading(false);
        onError?.(timeoutMsg);
      }
    }, 30000);

    // Cleanup function
    return () => {
      iframe.removeEventListener('load', handleLoad);
      iframe.removeEventListener('error', handleError);
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [loading, onError]);

  // Reset state when src changes
  useEffect(() => {
    setLoading(true);
    setError(null);
  }, [src]);

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    
    // Force iframe reload by updating src
    const iframe = iframeRef.current;
    if (iframe) {
      const currentSrc = iframe.src;
      iframe.src = '';
      setTimeout(() => {
        iframe.src = currentSrc;
      }, 100);
    }
  };

  return (
    <div className="relative w-full" style={{ minHeight: height }}>
      {/* Loading State */}
      {loading && !error && (
        <div className="absolute inset-0 flex justify-center items-center bg-gray-50 z-10 rounded-lg border">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Loading analytics dashboard...</p>
            <p className="text-gray-500 text-sm mt-1">This may take a few moments</p>
          </div>
        </div>
      )}
      
      {/* Error State */}
      {error && (
        <div className="absolute inset-0 flex justify-center items-center bg-red-50 z-10 rounded-lg border border-red-200">
          <div className="text-center p-6">
            <svg className="h-12 w-12 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h3 className="text-lg font-medium text-red-800 mb-2">
              Dashboard Loading Error
            </h3>
            <p className="text-red-700 mb-4 max-w-md">{error}</p>
            <div className="space-x-3">
              <button 
                onClick={handleRetry}
                className="bg-red-100 hover:bg-red-200 text-red-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Retry Loading
              </button>
              <button 
                onClick={() => window.open(src, '_blank')}
                className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Open in New Tab
              </button>
            </div>
            <p className="text-gray-500 text-xs mt-3">
              If the problem persists, contact support for assistance.
            </p>
          </div>
        </div>
      )}

      {/* Iframe */}
      <iframe
        ref={iframeRef}
        src={src}
        title={title}
        width="100%"
        height={height}
        frameBorder="0"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
        className={`border border-gray-200 rounded-lg shadow-sm transition-opacity duration-300 ${
          loading || error ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ 
          display: error ? 'none' : 'block',
          minHeight: height,
          backgroundColor: '#ffffff'
        }}
        // Security attributes
        credentialless="true"
        loading="lazy"
        // Additional attributes for better compatibility
        allow="fullscreen"
        referrerPolicy="strict-origin-when-cross-origin"
      />

      {/* Overlay for additional security (prevents clickjacking) */}
      {!loading && !error && (
        <div 
          className="absolute inset-0 pointer-events-none z-0"
          style={{ mixBlendMode: 'multiply', opacity: 0 }}
        />
      )}
    </div>
  );
};